<template>
  <pc-card
    :id="idPrefix"
    :outer-title="title"
    :inner-title="innerTitle"
    :text-left="textLeft"
    fill-width
    content-class="pa-0 ma-0"
    class="pc-data-table"
    :class="{ 'no-hover': noHover }"
    :no-border="noBorder"
  >
    <v-row dense>
      <v-col
        class="text-left"
        style="max-width: 400px"
        v-if="withSearchTextField"
      >
        <pc-text-field
          :id="setId('SearchField')"
          class="ml-4 mt-2"
          clearable
          dense
          append-icon="mdi-magnify"
          :label="searchLabel"
          v-model="searchText"
          hide-optional-text
          @clear="searchCleared()"
          @enter="searchWithText()"
          @blur="leftSearchField()"
          data-id="41a0f4ecd41439d5a6366f5f59bd1f5a"
        ></pc-text-field>
      </v-col>
      <v-col class="text-left flex-grow-1">
        <slot name="top-middle-content"></slot>
      </v-col>
      <v-col class="text-right pt-3 flex-grow-0">
        <div class="table-actions-style">
          <slot v-if="hasSlot('bulk-actions')" name="bulk-actions"></slot>
          <slot v-if="hasSlot('bulk-delete')" name="bulk-delete"></slot>
          <slot v-if="hasSlot('bulk-filter')" name="bulk-filter"></slot>
          <pc-button
            v-if="!withoutRefresh"
            @click="searchRequestHandler.search()"
            :disabled="isExporting || searchRequestHandler.isBusy"
            text
            small
            subtle
            tooltip="Refresh table data"
            data-id="d536320e9df36e866ed90722348ab892"
            ><pc-icon name="pc-refresh" size="16"></pc-icon>Refresh</pc-button
          >
          <pc-button
            v-if="withDataExport"
            @click="exportData"
            :disabled="
              isExporting || searchRequestHandler.isBusy || !showDataSection
            "
            text
            small
            subtle
            class="mr-4"
            tooltip="Download all data"
            data-id="a7d593ca86b5c46f20360a40ebfaadbe"
            ><pc-icon name="pc-download" size="16"></pc-icon>Export</pc-button
          >
        </div>
      </v-col>
    </v-row>

    <v-row class="py-0 my-0" v-if="hasSlot('action-buttons')">
      <v-col class="mt-0">
        <slot name="action-buttons"></slot>
      </v-col>
    </v-row>

    <v-data-table
      v-if="showDataSection"
      :headers="realHeaders"
      :items="searchRequestHandler.searchResult.results"
      item-key="id"
      v-model="selected"
      :options.sync="searchOptions"
      :loading="searchRequestHandler.isBusy"
      :calculate-widths="true"
      :loading-text="loadingText"
      :server-items-length="searchRequestHandler.searchResult.totalResults"
      :show-select="isSelectable"
      :color="$colors.penChecksRed"
      :items-per-page="showAllResults ? -1 : 10"
      :footer-props="{
        'items-per-page-options': showAllResults
          ? [-1]
          : [25, 50, 100, 250, 500],
        showCurrentPage: true,
      }"
      class="elevation-0"
      :item-class="itemClass || computeRowClass"
      :show-expand="isExpandable"
      :expanded.sync="expanded"
      @click:row="onRowClick"
    >
      <template slot="no-data">
        <v-row class="py-0 my-0" v-if="searchRequestHandler.lastSearchFailed">
          <v-col class="mt-0">
            <div :id="setId('SearchFailed')" class="search-failed">
              <div class="search-failed-title">Unable to load this list</div>
              <div class="search-failed-body">
                Something went wrong on our end. Refresh the page to try again.
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row class="py-0 my-0" v-else-if="hasSlot('no-data')">
          <v-col class="mt-0">
            <slot name="no-data"></slot>
          </v-col>
        </v-row>
      </template>

      <!-- just here to add id to tbody -->
      <template v-slot:body.prepend>
        <span :id="setId('Body')"></span>
      </template>

      <template
        v-slot:footer.page-text
        v-if="searchRequestHandler.fetchingFullCount"
      >
        <v-progress-circular
          v-if="searchRequestHandler.fetchingFullCount"
          indeterminate
          :color="$colors.penChecksTeal"
          size="12"
          class="mr-2"
        ></v-progress-circular>
      </template>

      <!-- Setup the format for columns that have defined the slot as part of table -->
      <template
        v-for="(_, scopedSlotName) in $scopedSlots"
        v-slot:[scopedSlotName]="item"
      >
        <slot :name="scopedSlotName" v-bind="item"></slot>
      </template>

      <!-- Expanded rows: the table owns the full-width cell and the page fills
           it through expanded-content (the same contract as
           PcAdminSearchResults). A consumer's own expanded-item slot, if
           given, is forwarded to v-data-table untouched instead. -->
      <template
        v-if="isExpandable && !hasSlot('expanded-item')"
        v-slot:expanded-item="{ headers, item }"
      >
        <td :colspan="headers.length">
          <slot name="expanded-content" v-bind:item="item"></slot>
        </td>
      </template>

      <!-- Vuetify's default expand chevron, re-created only to carry a data-id.
           A consumer's own item.data-table-expand slot still takes precedence. -->
      <template
        v-if="isExpandable && !hasSlot('item.data-table-expand')"
        v-slot:item.data-table-expand="{ isExpanded, expand }"
      >
        <v-icon
          class="v-data-table__expand-icon"
          :class="{ 'v-data-table__expand-icon--active': isExpanded }"
          data-id="315eefa82a584a5c832d23e51e356fcb"
          @click.stop="expand(!isExpanded)"
          >$expand</v-icon
        >
      </template>

      <!-- Setup the format for columns that have not defined the slot as part of table -->
      <template
        v-for="header in headersWithoutSlots"
        v-slot:[header.slotName]="{ item, value }"
      >
        <!-- Callback for each cell -->
        <template v-if="header.slotName == 'item.recordNumber'">
          <slot v-bind:item="item" name="item-record-number">
            <small
              :style="'color: ' + $colors.penChecksDarkGray"
              :key="header.value"
            >
              {{
                1 +
                searchRequestHandler.searchResult.results.indexOf(item) +
                (searchRequestHandler.searchResult.page - 1) *
                  searchRequestHandler.searchResult.resultsPerPage
              }}
            </small>
          </slot>
        </template>

        <template v-else-if="header.slotName == 'item.dataErrors'">
          <slot v-bind:item="item" name="item-data-errors">
            <pc-error-bullet
              :fieldsRequiringDataFix="item.fieldsRequiringDataFix"
              :key="header.value"
            ></pc-error-bullet>
          </slot>
        </template>

        <template v-else-if="header.slotName == 'item.actions'">
          <div class="itemActionButtonContainer" :key="header.value">
            <slot v-bind:item="item" name="item-action-buttons"></slot>

            <pc-button
              @click="clickedItem(item)"
              @middleClick="openInNewTab(item)"
              @ctrlClick="openInNewTab(item)"
              :key="header.value"
              :color="$colors.penChecksGray"
              class="hover-action-button"
              small
              data-id="bce3e6c79c8e9d2205a3e79b63d2d998"
            >
              <pc-icon name="pc-arrow-right" size="14"></pc-icon>
            </pc-button>
          </div>
        </template>

        <template v-else-if="header.slotName == 'item.noticeDisplayName'">
          <slot v-bind:item="item" name="item-notice-display-name">
            <pc-notice-name
              v-if="item.noticeId !== null"
              :noticeKey="item.noticeDisplayName"
              isPremierNotice
            >
            </pc-notice-name>

            <div
              v-else-if="item.documentTemplateId !== null"
              :key="item.documentTemplateId"
            >
              {{ item.documentTemplateDisplayName }}
            </div>
          </slot>
        </template>

        <pc-copyable
          v-else-if="header.searchableField.resultDataType == 'internalId'"
          :key="'internalId_' + header.value"
          >{{ value }}</pc-copyable
        >

        <pc-copyable
          v-else-if="header.searchableField.resultDataType == 'uuid'"
          :key="'uuid_' + header.value"
          >{{ value }}</pc-copyable
        >

        <pc-copyable
          v-else-if="header.searchableField.resultDataType == 'friendlyId'"
          :key="'friendlyId_' + header.value"
          >{{ value | friendlyId }}</pc-copyable
        >

        <template
          v-else-if="header.searchableField.resultDataType == 'objectLink'"
        >
          <a
            :href="objectLink(item, header.searchableField)"
            :key="'objectLink_' + header.value"
            data-id="b608a43465b7d0891df916612f8f03a0"
            >{{ value }}</a
          >
        </template>

        <pc-status-chip
          v-else-if="
            header.searchableField.resultDataType == 'statusChip' && value
          "
          :key="'statusChip_' + header.value"
          :value="value"
          :statusDetail="item && item.substatus ? item.substatus : ''"
          :entity="entityType"
          x-small
        ></pc-status-chip>

        <pc-cip-risk-chip
          v-else-if="
            header.searchableField.resultDataType == 'riskChip' && value
          "
          :key="'riskChip_' + header.value"
          :level="value"
        ></pc-cip-risk-chip>

        <pc-status-detail
          v-else-if="
            header.searchableField.resultDataType == 'substatusBullet' && value
          "
          :key="'substatusBullet_' + header.value"
          :detail="value"
          :status="item && item.status ? item.status : undefined"
        >
        </pc-status-detail>

        <template
          v-else-if="
            header.searchableField.resultDataType == 'boolean' ||
            header.searchableField.resultDataType == 'centeredBoolean'
          "
        >
          <span
            v-if="value === true || value === 1"
            :key="'booleanf_' + header.value"
            >Yes</span
          >
          <span
            v-else-if="value === false || value === 0"
            :key="'booleant_' + header.value"
            >No</span
          >
        </template>

        <pc-error-bullet
          v-else-if="header.searchableField.resultDataType == 'flagBullet'"
          :key="'flagBullet_' + header.value"
          :fieldsRequiringDataFix="value"
        ></pc-error-bullet>

        <template
          v-else-if="header.searchableField.resultDataType == 'titleText'"
          >{{ value | titleCase }}</template
        >

        <pc-tax-id
          :value="value"
          :key="'titleText_' + header.value"
          :taxIdType="item.taxIdType"
          v-else-if="
            header.searchableField.resultDataType == 'socialSecurityNumber'
          "
        ></pc-tax-id>

        <template v-else-if="header.searchableField.resultDataType == 'month'">
          <!-- Display month only: eg "Dec 2020" -->
          {{ value | formatDate }}
        </template>

        <template v-else-if="header.searchableField.resultDataType == 'date'">
          <!-- Display date only: eg "Dec 12, 2020" -->
          {{ value | formatDate }}
        </template>

        <template
          v-else-if="
            value && value.searchResultTitle && typeof value == 'object'
          "
          >{{ value.searchResultTitle }}</template
        >

        <template
          v-else-if="header.searchableField.resultDataType == 'currencyAmount'"
          >{{ value | currency }}</template
        >

        <template
          v-else-if="
            header.searchableField.resultDataType == 'numberWithCommas'
          "
          >{{ value | numberWithCommas }}</template
        >

        <template
          v-else-if="header.searchableField.resultDataType == 'percentage'"
          >{{ value | percentage }}</template
        >

        <template
          v-else-if="header.searchableField.resultDataType == 'storedFile'"
        >
          <template v-if="value">
            <a
              :href="'/files/' + value + '/download'"
              target="_blank"
              :key="header.value"
              data-id="eae5e56087922856c8250f74372c5cec"
              >Download</a
            >
          </template>
        </template>

        <template
          v-else-if="header.searchableField.resultDataType == 'phoneNumber'"
          >{{ value | phoneNumber }}</template
        >

        <template
          v-else-if="header.searchableField.resultDataType == 'dateTime'"
        >
          <!-- Display date & time.  eg. "Dec 12, 2020, 5:12 pm" -->
          {{ value | formatDate }}
        </template>

        <div
          v-else-if="header.searchableField.resultDataType == 'relativeDate'"
          :key="header.value"
        >
          <!-- show the date as a relative date format ('MMM D, YYYY, hh:mm a') -->
          {{ value | formatDate }}
          <br />
          <small>{{ value | relativeDate }}</small>
        </div>

        <template
          v-else-if="header.searchableField.resultDataType == 'textOrNotSet'"
          >{{ value || "Not yet set" }}</template
        >

        <template v-else>
          <!-- Plain text return -->
          {{ value }}
        </template>
      </template>

      <template
        v-slot:item.actions="{ item }"
        v-if="($listeners && $listeners['click:row']) || hasSlot('actions')"
      >
        <div v-if="hasSlot('actions')" class="d-flex">
          <slot name="actions" v-bind:item="item"></slot>
        </div>
        <div v-else>
          <pc-button
            class="action-buttons"
            :color="$colors.penChecksGray"
            small
            @click="handleActionArrowClick($event, item)"
            @middleClick="$emit('middleClick:row', item)"
            @ctrlClick="handleActionArrowCtrlClick($event, item)"
            data-id="d3d256f0086ecea191ec76cf48e3284e"
          >
            <pc-icon name="pc-arrow-right" size="14"></pc-icon>
          </pc-button>
        </div>
      </template>
    </v-data-table>
  </pc-card>
</template>

<script>
import SearchRequestHandler from "@/shared/lib/client-sdk/search/search-request-handler";

export default {
  props: {
    searchRequestHandler: {
      type: SearchRequestHandler,
      required: true,
    },
    title: {
      type: String,
    },
    innerTitle: {
      type: String,
      required: false,
    },
    exportTitle: {
      type: String,
      required: false,
    },
    textLeft: {
      type: Boolean,
      required: false,
      default: false,
    },
    headers: {
      type: Array,
      required: true,
    },
    id: {
      type: String,
    },
    entityType: {
      type: String,
      default: null,
    },
    noHover: {
      type: Boolean,
    },
    loadingText: {
      type: String,
      default: "Searching...",
    },
    withoutRefresh: {
      type: Boolean,
    },
    withSearchTextField: {
      type: Boolean,
    },
    withDataExport: {
      type: Boolean,
    },
    withBackgroundExport: {
      type: Boolean,
      default: false,
    },
    initialSearchText: {
      type: String,
    },
    searchLabel: {
      type: String,
      default: "Search",
    },
    noBorder: {
      type: Boolean,
      default: false,
    },
    localStorageKey: {
      type: String,
    },
    idPrefix: {
      type: String,
    },
    isSelectable: {
      type: Boolean,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    showAllResults: {
      type: Boolean,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    itemClass: {
      type: Function,
      default: null,
    },
    showDataSection: {
      type: Boolean,
      default: true,
    },
    clickableRows: {
      type: Boolean,
      default: false,
    },
    // Opt-in row expansion: shows Vuetify's expand chevron and renders the
    // consumer's expanded-content slot (props: item) in a full-width cell.
    // Expanded rows collapse whenever a new result set arrives.
    isExpandable: {
      type: Boolean,
      default: false,
    },
  },

  data: function () {
    return {
      searchText: "",
      isExporting: false,
      initialSearchComplete: false,
      unfilteredSearchResult: null,
      bypassSearchOptionsWatcher: true,
      searchOptions: {
        page: 1,
        itemsPerPage: 25,
        mustSort: true,
        sortBy: [],
        sortDesc: [],
      },
      selected: [],
      expanded: [],
    };
  },

  watch: {
    initialSearchText: function () {
      this.searchText = this.initialSearchText ? this.initialSearchText : "";
    },
    searchRequestHandler: function () {
      this.initializeSearchRequestHandler();
    },
    searchOptions: {
      handler: function () {
        if (
          !this.bypassSearchOptionsWatcher &&
          this.searchRequestHandler.hasSearchedAtLeastOnce &&
          !this.searchRequestHandler.isBusy
        ) {
          this.searchRequestHandler.page = this.searchOptions.page;
          this.searchRequestHandler.resultsPerPage =
            this.searchOptions.itemsPerPage;

          this.searchRequestHandler.sortBy = null;
          this.searchRequestHandler.sortDirection = null;

          if (this.searchOptions.sortBy.length > 0) {
            var sortByHeader = this.realHeaders.find(
              (o) => o.value == this.searchOptions.sortBy[0]
            );

            if (sortByHeader) {
              var fieldName =
                sortByHeader.searchFieldName || sortByHeader.value;
              this.searchRequestHandler.sortBy = fieldName;

              this.searchRequestHandler.sortDirection =
                this.searchOptions.sortDesc.length > 0
                  ? this.searchOptions.sortDesc[0]
                    ? "desc"
                    : "asc"
                  : null;
            }
          }

          this.searchRequestHandler.search();
        }

        this.bypassSearchOptionsWatcher = false;
      },
      deep: true,
    },
    selectedItems: function (items) {
      this.selected = items;
    },
    selected: function () {
      this.$emit("select", this.selected);
    },
    "searchRequestHandler.searchResult.totalResults": function (count) {
      this.$emit("total", count);
    },
  },

  computed: {
    localStorageKeyPrefix: function () {
      var url = window.location.href;

      if (url.includes("?")) url = url.split("?")[0];

      if (url.includes("#query:")) url = url.split("#query:")[0];

      // Remove any IDs from the URL, so it doesn't change each time
      url = url.replace(/\b[a-zA-Z0-9]{32}\b/gi, "{id}");

      var requestUrl = this.searchRequestHandler.requestUrl;

      if (requestUrl.includes("?")) requestUrl = requestUrl.split("?")[0];

      if (requestUrl.includes("#query:"))
        requestUrl = requestUrl.split("#query:")[0];

      // Remove any IDs from the URL, so it doesn't change each time
      requestUrl = requestUrl.replace(/\b[a-zA-Z0-9]{32}\b/gi, "{id}");

      return (
        "dataTableSearch_" +
        this.searchRequestHandler.modelClassName +
        "_" +
        url +
        "_" +
        requestUrl +
        "_"
      );
    },
    realHeaders: function () {
      var newHeaders = JSON.parse(JSON.stringify(this.headers));

      for (var i = 0; i < newHeaders.length; i++) {
        var header = newHeaders[i];
        // An explicit `sortable: false` on a declared header wins. Some columns carry a
        // searchFieldName only so the backend can filter on them (or so the column picker
        // can find them), while the backend marks the field .NotSortable() because sorting
        // it would push an expensive expression into ORDER BY.
        header.sortable =
          header.sortable === false
            ? false
            : this.sortable || Boolean(header.searchFieldName);
      }

      if (
        (this.$listeners && this.$listeners["click:row"]) ||
        this.hasSlot("actions")
      ) {
        newHeaders.push({
          text: " ",
          sortable: false,
          value: "actions",
          align: "end",
        });
      }

      return newHeaders;
    },
    // Get the list of columns that have not defined slot into the table
    headersWithoutSlots: function () {
      var headersWithoutSlotsArray = [];

      for (var index = 0; index < this.headers.length; index++) {
        const field = this.headers[index];

        // Normally the Slot name has the format 'item.' concatenated with the field value
        if (!this.hasSlot("item." + field.value)) {
          headersWithoutSlotsArray.push(field);
        }
      }

      return headersWithoutSlotsArray;
    },
  },

  methods: {
    hasSlot(name) {
      return !!this.$slots[name] || !!this.$scopedSlots[name];
    },
    setId: function (idSufix) {
      return this.idPrefix && idSufix ? this.idPrefix + idSufix : undefined;
    },
    initializeSearchRequestHandler: function () {
      if (this.searchText && this.searchText.trim() != "") {
        this.searchRequestHandler.generalFilter = this.searchText;
      }
      this.searchRequestHandler.tableComponentCallback =
        this.searchComplete.bind(this);
    },
    leftSearchField: function () {
      if (!this.searchText || this.searchText.trim() == "") {
        this.resetSearchFilter();
      }
    },
    exportData: function () {
      var customColumns = "";
      var separator = "";
      for (var index = 0; index < this.headers.length; index++) {
        const field = this.headers[index];

        var fieldName = !field.column ? field.value : field.column;
        customColumns = customColumns + separator + fieldName;
        separator = ",";
      }

      this.isExporting = true;
      this.$overlay.show("Preparing data export...");

      let requestData = {
        customColumns: customColumns,
      };

      this.searchRequestHandler.queueExport(
        requestData,
        this.withBackgroundExport,
        (success, exportDownloadUri) => {
          this.$overlay.hide();

          if (success) {
            if (exportDownloadUri) {
              let url =
                window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
                "/public" +
                exportDownloadUri;
              if (
                window.location.hostname.includes(
                  "unclaimedretirementbenefits.com"
                )
              ) {
                url = url.replace(
                  "penchecks.com",
                  "unclaimedretirementbenefits.com"
                );
              }

              window.location.href = url;
            } else {
              this.$modals.alert({
                title: "Search Request Export Queued",
                text: "You will receive an email when your data export is ready to be downloaded.",
              });
            }
          }
          this.isExporting = false;
        }
      );
    },
    searchWithText: function () {
      if (!this.searchText || this.searchText.trim() == "") {
        this.resetSearchFilter();
      } else {
        this.searchRequestHandler.generalFilter = this.searchText;
        this.searchRequestHandler.search();
      }
    },
    searchCleared: function () {
      this.resetSearchFilter();
    },
    searchComplete: function () {
      this.initialSearchComplete = true;
      // New rows (sort, page, filter, refresh, a page's own re-search): drop
      // the expansion so it cannot linger on rows it no longer describes.
      if (this.isExpandable) this.expanded = [];
      this.updateSearchOptionsFromResult();
    },

    updateSearchOptionsFromResult: function () {
      this.bypassSearchOptionsWatcher = true;
      this.$nextTick(() => {
        this.searchOptions.page = this.searchRequestHandler.searchResult.page;
        this.searchOptions.itemsPerPage =
          this.searchRequestHandler.searchResult.resultsPerPage;

        this.searchOptions.sortBy = [];
        this.searchOptions.sortDesc = [];

        if (this.searchRequestHandler.searchResult.sortBy) {
          var field = this.searchRequestHandler.getSearchableFieldByName(
            this.searchRequestHandler.searchResult.sortBy
          );

          if (field) {
            var header = this.realHeaders.find(
              (o) =>
                field.fieldName == o.searchFieldName ||
                field.resultKey == o.value
            );

            if (header) {
              this.searchOptions.sortBy = [header.value];
              this.searchOptions.sortDesc =
                this.searchRequestHandler.searchResult.sortDirection
                  .toLowerCase()
                  .startsWith("desc")
                  ? [header.value]
                  : [];
            }
          }
        }
      });
    },
    objectLink: function (item, searchableField) {
      var idField = searchableField.objectLinkSearchableFieldWithId;
      var modelType = idField.substr(0, idField.length - 2);
      var modelClass = this.searchRequestHandler.searchableModels[modelType];
      var model = new modelClass();

      model.id = item[idField.toCamelCase()];
      return model.getAdminPath();
    },
    resetSearchFilter: function () {
      if (this.searchRequestHandler.generalFilter == null) return;
      this.searchRequestHandler.generalFilter = null;
      this.searchRequestHandler.search();
    },

    onRowClick(item) {
      if (this.clickableRows) {
        this.$emit("click:row", item);
      }
    },

    handleActionArrowClick(event, item) {
      // Clicking the action arrow bubbles to the row's native click, which
      // would fire click:row a second time and cancel the first navigation.
      event.stopPropagation();
      this.$emit("click:row", item);
    },

    handleActionArrowCtrlClick(event, item) {
      // No stopPropagation here on purpose: the click needs to reach the row,
      // which is what navigates on tables with no middleClick:row listener.
      if (!this.$listeners || !this.$listeners["middleClick:row"]) return;

      event.stopPropagation();
      this.$emit("middleClick:row", item);
    },

    computeRowClass() {
      return this.clickableRows ? "clickable-row" : "";
    },
  },

  mounted() {
    this.searchText = this.initialSearchText ? this.initialSearchText : "";
    this.initializeSearchRequestHandler();
  },
};
</script>

<style lang="scss">
.search-failed {
  margin: 96px 16px 96px 16px;

  .search-failed-title {
    color: $penChecksDark;
    font-size: 18px;
    font-weight: 600;
  }
  .search-failed-body {
    margin-top: 16px;
    color: $penChecksDarkGray;
    font-size: 14px;
    font-weight: 400;
  }
}

.pc-data-table {
  min-width: 704px;

  .v-card__text {
    padding: 12px;
    margin: 0px;

    .v-progress-linear .primary {
      background-color: $penChecksTeal !important;
      border-color: $penChecksTeal !important;
      color: $penChecksTeal !important;
    }
  }

  tr:hover .action-buttons .v-btn {
    background-color: $penChecksTeal !important;
  }

  tbody td {
    cursor: default !important;
  }

  &.no-hover {
    .v-data-table tr:hover {
      background-color: transparent !important;
    }
  }

  &:not(.no-hover) {
    .v-data-table tr:hover {
      background-color: $penChecksLight !important;
    }
  }

  .v-icon.mdi-checkbox-marked {
    color: $penChecksTeal;
  }

  .v-icon.mdi-minus-box {
    color: $penChecksTeal;
  }

  .table-actions-style {
    display: flex;
    float: right;
  }

  .clickable-row td {
    cursor: pointer !important;
  }
}

@media print {
  .pc-data-table {
    min-width: unset;
  }
}
</style>
